import * as React from "react";
import { Query, SortDirection } from "../../interfaces";
import { Grid, Link, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { AppBody } from "../AppBody";
import FinancialReportingSearchForm from "./FinancialReportingSearchForm";
import useSortProperties from "../../hooks/useSortProperties";
import FinancialReportingLevelOne from "../../containers/FinancialReportingLevelOne";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { appHeaderTitle } from "../../common/services/Utilities";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { Link as RouterLink } from "react-router-dom";
import PFATheme from "../../PFATheme";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import BackButton from "../../containers/BackButton";

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  resetState: () => void;
  updateFilters: (cert: string) => void;
}

export default function FinancialReporting({
  appState,
  updateSort,
  resetState,
  updateFilters,
}: Props) {
  React.useEffect(() => {
    // resets state to default when navigating to URL with no query params
    // or adds the cert in the query to the appState
    if (!window.location.search) {
      resetState();
    } else {
      const params = new URLSearchParams(window.location.search);
      const cert = params.get("cert");
      cert && updateFilters(cert);
    }
  }, [resetState, updateFilters]);
  const introParagraph = (
    <>
      {appHeaderTitle("financialReport")}
      <Typography variant="body2" id="financial-reporting-description">
        Find institutions based on financial, regulatory, and other
        characteristics. Create comprehensive financial reports (e.g., FFIEC
        Call Report; Assets, Liabilities, & Capital; Income & Expenses; etc.)
        using quarterly data since 1992. If you need assistance, please see the{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>Help Section</RouterLink>{" "}
        for tutorials to get you started.
      </Typography>
      <Typography variant="body2" id="financial-reporting-description">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=FinRegData"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );
  const sortProperties = useSortProperties(appState, updateSort);

  useInitialPageLoad("financialReport");

  const prevUrl = sessionStorage.getItem("prevUrl");
  return (
    <ThemeProvider theme={PFATheme}>
      <>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Breadcrumbs currentPageBreadCrumbTitle="Financial/Regulatory Search & Reporting" />
          </Grid>
        </Grid>
        <CloudFoundryBanner />
        <AppBody
          introText={introParagraph}
          searchForm={<FinancialReportingSearchForm appState={appState} />}
          backButton={
            <BackButton
              app="FinancialReporting"
              buttonLabel={
                prevUrl?.includes("bankfind")
                  ? "Back to Institution Details"
                  : "Back to Financial Reporting"
              }
              variant="dark"
            />
          }
        >
          <FinancialReportingLevelOne sortProperties={sortProperties} />
        </AppBody>
      </>
    </ThemeProvider>
  );
}
